import React, { useEffect, useRef } from 'react';
import './StaticParticles.css';

const StaticParticles = ({ 
  count = 150,
  color = '#FDB241',
  opacity = 0.6,
  sway = 0.5
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d', { alpha: true });
    const container = canvas.parentElement;
    
    let width = container.offsetWidth;
    let height = container.offsetHeight;
    
    canvas.width = width;
    canvas.height = height;

    // Парсим цвет один раз
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 253, g: 178, b: 65 };
    };
    const rgb = hexToRgb(color);

    // Генерируем частицы
    const particles = [];
    for (let i = 0; i < count; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      particles.push({
        baseX: x,
        baseY: y,
        x, y,
        radius: Math.random() * 2 + 0.5,
        baseOpacity: Math.random() * 0.4 + 0.3,
        pulseSpeed: Math.random() * 0.5 + 0.5,
        pulsePhase: Math.random() * Math.PI * 2,
        swayPhaseX: Math.random() * Math.PI * 2,
        swayPhaseY: Math.random() * Math.PI * 2,
        swaySpeedX: (Math.random() * 0.5 + 0.5) * 0.001,
        swaySpeedY: (Math.random() * 0.5 + 0.5) * 0.001
      });
    }

    // Resize
    const handleResize = () => {
      const newW = container.offsetWidth;
      const newH = container.offsetHeight;
      particles.forEach(p => {
        p.baseX = (p.baseX / width) * newW;
        p.baseY = (p.baseY / height) * newH;
        p.x = p.baseX;
        p.y = p.baseY;
      });
      width = newW;
      height = newH;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);

    // Анимация — 30fps cap + cancelAnimationFrame в cleanup
    let animId;
    let lastTime = 0;
    const fpsInterval = 1000 / 30;

    const animate = (currentTime) => {
      animId = requestAnimationFrame(animate);

      const elapsed = currentTime - lastTime;
      if (elapsed < fpsInterval) return;
      lastTime = currentTime - (elapsed % fpsInterval);

      ctx.clearRect(0, 0, width, height);

      // shadowBlur рисуем один раз для всех частиц одного размера
      // группируем по approximate radius для минимизации state-switches
      ctx.save();
      ctx.shadowColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
      
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // пульс
        p.pulsePhase += p.pulseSpeed * 0.02;
        const pulsation = Math.sin(p.pulsePhase) * 0.3;
        const finalOpacity = (p.baseOpacity + pulsation) * opacity;

        // sway
        if (sway > 0) {
          p.swayPhaseX += p.swaySpeedX;
          p.swayPhaseY += p.swaySpeedY;
          p.x = p.baseX + Math.sin(p.swayPhaseX) * 2 * sway;
          p.y = p.baseY + Math.cos(p.swayPhaseY) * 2 * sway;
        }

        // glow circle через shadowBlur (дешевле radialGradient)
        ctx.shadowBlur = p.radius * 6;
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${finalOpacity * 0.6})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();

        // яркая точка в центре (без shadow)
        ctx.shadowBlur = 0;
        ctx.fillStyle = `rgba(255, 255, 255, ${finalOpacity * 0.7})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    };

    animate(0);

    // Cleanup — отменяем RAF + resize
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, [count, color, opacity, sway]);

  return <canvas ref={canvasRef} className="static-particles-canvas" />;
};

export default StaticParticles;
